import { cn } from "@/lib/utils";

interface CompactTrace {
  traceId: string;
  hasErrors: boolean;
  request: string;
  target: string | null;
  timestamp: string;
  duration: number;
  spanCount: number;
  requestTarget: string;
}

interface CompactTraceItemProps {
  trace: CompactTrace;
  onClick?: () => void;
}

export function CompactTraceItem({ trace, onClick }: CompactTraceItemProps) {
  return (
    <button
      type="button"
      className={cn(
        "grid gap-2 px-3 py-2 text-sm border-t hover:bg-muted/50 cursor-pointer transition-colors w-full text-left",
        // Responsive grid: minimal (3 cols) → small (4 cols)
        "grid-cols-[32px_1fr_60px]",
        "@[400px]:grid-cols-[32px_2fr_1fr_60px]",
      )}
      onClick={onClick}
    >
      {/* Status Column */}
      <div className="flex items-center justify-center">
        <div
          className={cn(
            "w-2 h-2 rounded-full",
            trace.hasErrors ? "bg-destructive" : "bg-success",
          )}
        />
      </div>

      {/* Request Column (visible on larger screens) */}
      <div className="hidden @[400px]:flex items-center min-w-0">
        <span className="font-mono text-xs truncate" title={trace.request}>
          {trace.request}
        </span>
      </div>

      {/* Target Column (visible on larger screens) */}
      <div className="hidden @[400px]:flex items-center min-w-0">
        {trace.target ? (
          <span
            className="text-xs text-muted-foreground truncate"
            title={trace.target}
          >
            {trace.target}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">--</span>
        )}
      </div>

      {/* Combined Request/Target Column (visible on minimal screens) */}
      <div className="flex @[400px]:hidden items-center min-w-0">
        <div className="min-w-0">
          <div
            className="font-mono text-xs truncate"
            title={trace.requestTarget}
          >
            {trace.requestTarget}
          </div>
          <div className="text-xs text-muted-foreground">
            {trace.spanCount} span{trace.spanCount !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Time Column */}
      <div className="flex items-center justify-end">
        <div className="text-right">
          <div className="font-mono text-xs">{trace.timestamp}</div>
          <div className="text-xs text-muted-foreground hidden @[400px]:block">
            {trace.duration}ms
          </div>
        </div>
      </div>
    </button>
  );
}
